import { z } from 'zod';
import { CLINIC_SIGNAL_NAMES } from '../types/clinic-signals';

/**
 * The automation-rule contract, shared between the backend (which validates
 * `POST /rules` and `PATCH /rules/:id` against it) and the Clinic Portal (which
 * derives its types from it rather than restating them).
 *
 * Pure Zod, zero other imports, so nothing backend-shaped leaks into browser
 * bundles — same constraint as `lib/list.ts`.
 *
 * These rules are persisted as JSON and later *executed* by
 * `backend/src/services/rules/{conditions,actions}.ts`, so this schema is the
 * boundary between user input and code that sends mail, writes labels and spends
 * quota. The TS unions in those two files mirror this schema; keep them in
 * lockstep, or better, derive them from `z.infer` here.
 */

export const RuleTriggerTypeSchema = z.enum(['on_ingest', 'scheduled']);

export const RuleConditionOperatorSchema = z.enum([
  'equals',
  'notEquals',
  'contains',
  'in',
  'gt',
  'lt',
  'matchesDomain',
]);

/**
 * `field` stays an open string: the evaluator resolves both real fact keys and
 * derived ones (`senderDomain`, `hoursSinceLastActivity`), and an unknown field
 * simply fails to match rather than erroring.
 */
export const RuleConditionSchema = z.object({
  field: z.string(),
  operator: RuleConditionOperatorSchema,
  value: z.union([z.string(), z.number(), z.array(z.string())]),
});

/**
 * `add_label`/`remove_label` accept both `tagId` (the mail-tags shape, issue #44)
 * and `labelId` (a raw provider label id, pre-redesign) as optional. Both shapes
 * are live: rules saved before the redesign still carry `labelId` in their stored
 * JSON, and expand/contract means that JSON is never rewritten underneath them.
 *
 * An action with neither is accepted here and fails at execution — the executor
 * returns 'Action has no tagId or labelId'. Every *other* action's params are
 * required, because their executors write the value straight into the database
 * with no guard.
 */
export const RuleActionSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('add_label'),
    params: z.object({
      tagId: z.number().int().positive().optional(),
      labelId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('remove_label'),
    params: z.object({
      tagId: z.number().int().positive().optional(),
      labelId: z.string().optional(),
    }),
  }),
  z.object({
    type: z.literal('create_task'),
    params: z.object({
      title: z.string().optional(),
      assignedTo: z.number().int().positive().optional(),
      priority: z.number().int().optional(),
      dueInHours: z.number().optional(),
      /**
       * Route the task to a named Microsoft To Do list in the assignee's own
       * account. Optional and additive — without it the task goes to their default
       * destination, and with no destination nothing is mirrored.
       */
      todoListId: z.string().min(1).optional(),
    }),
  }),
  z.object({
    type: z.literal('link_contact'),
    params: z.object({ contactId: z.number().int().positive() }),
  }),
  z.object({
    type: z.literal('set_category'),
    params: z.object({ categoryId: z.number().int().positive() }),
  }),
  z.object({
    type: z.literal('set_priority'),
    params: z.object({ weight: z.number() }),
  }),
  z.object({
    type: z.literal('auto_reply'),
    params: z.object({ cannedResponseId: z.number().int().positive() }),
  }),
]);

/**
 * A semantic predicate: a rule condition answered by a Jev signal rather than a stored
 * field.
 *
 * Versioned, and the version is part of what is saved. A signal's version is bumped when
 * its *question* changes, so a rule written against version 1 of `response_needed` must
 * not silently start matching on version 2's answers — a threshold calibrated against one
 * question applied to a different one is a rule that quietly does the wrong thing.
 *
 * `minProbability` is required and has no default. A semantic predicate without a stated
 * confidence bar is a rule whose author never decided how sure they wanted to be, and
 * picking a number for them is how an automation surprises someone.
 */
export const RuleSemanticPredicateSchema = z.object({
  signal: z.enum(CLINIC_SIGNAL_NAMES),
  /** The signal version this rule was written against. */
  signalVersion: z.number().int().positive(),
  /**
   * The answer that counts as a match. A string for a choice signal's label, `true` for
   * a yes/no one.
   */
  expected: z.union([z.string(), z.boolean()]),
  /** 0..1. The rule does not fire below this. */
  minProbability: z.number().min(0).max(1),
});

/**
 * What a semantic rule is allowed to do.
 *
 * `suggest` presents an evidence-backed suggestion; `draft` prepares a reviewable reply.
 * Neither sends mail, changes a mailbox, or closes anything. There is no third mode in
 * this release, and the absence is the safety property — a semantic workflow cannot
 * acquire send authority by configuration, only by a code change that adds a mode here.
 */
export const RuleActionModeSchema = z.enum(['suggest', 'draft']);

export const RuleActionTypeSchema = z.enum([
  'add_label',
  'remove_label',
  'create_task',
  'link_contact',
  'set_category',
  'set_priority',
  'auto_reply',
]);

/**
 * `POST /rules`.
 *
 * The arrays are intentionally not `.min(1)`: `RulesService.create` already rejects
 * empty conditions/actions with its own 400 and message, and duplicating that here
 * would change the response shape clients currently get for that case.
 */
export const CreateRuleSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  triggerType: RuleTriggerTypeSchema.optional(),
  conditions: z.array(RuleConditionSchema),
  actions: z.array(RuleActionSchema),
  priority: z.number().int().optional(),
  stopOnMatch: z.boolean().optional(),
  isEnabled: z.boolean().optional(),
  /**
   * Semantic predicates, evaluated *after* the static conditions above.
   *
   * Ordering is a cost decision as much as a correctness one: static eligibility is free
   * and semantic evaluation needs a cached signal, so a rule that cannot match on its
   * static conditions never looks at a signal at all.
   */
  semanticPredicates: z.array(RuleSemanticPredicateSchema).optional(),
  /**
   * Required once a rule carries a semantic predicate, and rejected otherwise — see
   * `validateSemanticRule`. A semantic rule with no declared mode would fall through to
   * whatever its actions do, which for `auto_reply` is sending mail.
   */
  actionMode: RuleActionModeSchema.optional(),
});

/**
 * `PATCH /rules/:id`. Every field optional — the handler applies only the keys
 * that are present, so `{}` is a valid no-op patch.
 */
export const UpdateRuleSchema = CreateRuleSchema.partial();

/**
 * Actions a *semantic* rule may carry.
 *
 * `auto_reply` is absent deliberately. Static auto-reply stays exactly as it was — a
 * separately enabled, capability-gated, loop-protected path — and a Jev signal must not
 * become a way to reach it. In this release no semantic workflow sends external mail.
 */
export const SEMANTIC_SAFE_ACTION_TYPES = [
  'add_label',
  'remove_label',
  'create_task',
  'link_contact',
  'set_category',
  'set_priority',
] as const;

export type SemanticRuleValidationError =
  | { code: 'mode_required'; message: string }
  | { code: 'mode_without_predicate'; message: string }
  | { code: 'unsupported_action'; message: string; actionType: string }
  | { code: 'unknown_signal'; message: string; signal: string }
  | { code: 'stale_signal_version'; message: string; signal: string };

/**
 * Validates a rule's semantic parts at save time.
 *
 * Rejecting at save rather than at execution is the whole point: a rule that fails when
 * it fires fails silently, at 3am, against real mail. A rule that fails when it is
 * written fails in front of the person who can fix it.
 *
 * `currentSignalVersions` is passed in rather than imported so this stays a pure function
 * the portal can call before it submits the form.
 */
export function validateSemanticRule(
  rule: {
    semanticPredicates?: Array<{ signal: string; signalVersion: number }> | null;
    actionMode?: string | null;
    actions: Array<{ type: string }>;
  },
  currentSignalVersions: Record<string, number>
): SemanticRuleValidationError[] {
  const errors: SemanticRuleValidationError[] = [];
  const predicates = rule.semanticPredicates ?? [];

  if (predicates.length > 0 && !rule.actionMode) {
    errors.push({
      code: 'mode_required',
      message:
        'A rule with a semantic condition must say whether it suggests or drafts. Without that it would run its actions directly.',
    });
  }

  if (predicates.length === 0 && rule.actionMode) {
    errors.push({
      code: 'mode_without_predicate',
      message: 'This rule has no semantic condition, so it does not need a suggest or draft mode.',
    });
  }

  for (const predicate of predicates) {
    const current = currentSignalVersions[predicate.signal];
    if (current === undefined) {
      errors.push({
        code: 'unknown_signal',
        message: `"${predicate.signal}" is not a signal this workspace can use.`,
        signal: predicate.signal,
      });
      continue;
    }
    if (predicate.signalVersion !== current) {
      errors.push({
        code: 'stale_signal_version',
        message: `"${predicate.signal}" has changed since this rule was written, so its answers mean something different. Review the rule and save it again.`,
        signal: predicate.signal,
      });
    }
  }

  if (predicates.length > 0) {
    for (const action of rule.actions) {
      if (!(SEMANTIC_SAFE_ACTION_TYPES as readonly string[]).includes(action.type)) {
        errors.push({
          code: 'unsupported_action',
          message: `A rule with a semantic condition cannot ${describeAction(action.type)}. That needs a person.`,
          actionType: action.type,
        });
      }
    }
  }

  return errors;
}

function describeAction(type: string): string {
  if (type === 'auto_reply') return 'send a reply';
  return `run "${type}"`;
}

export type RuleSemanticPredicateDef = z.infer<typeof RuleSemanticPredicateSchema>;
export type RuleActionMode = z.infer<typeof RuleActionModeSchema>;
export type RuleTriggerType = z.infer<typeof RuleTriggerTypeSchema>;
export type RuleConditionOperator = z.infer<typeof RuleConditionOperatorSchema>;
export type RuleConditionDef = z.infer<typeof RuleConditionSchema>;
export type RuleActionDef = z.infer<typeof RuleActionSchema>;
export type RuleActionType = z.infer<typeof RuleActionTypeSchema>;
export type CreateRuleBody = z.infer<typeof CreateRuleSchema>;
export type UpdateRuleBody = z.infer<typeof UpdateRuleSchema>;

/**
 * True when an action carries every param its executor requires.
 *
 * Shared with the Clinic Portal's rule form so the UI can block an incomplete
 * action before it is sent, rather than relying on a 400. `add_label`/`remove_label`
 * are the deliberate exception — see `RuleActionSchema`.
 */
export function isActionComplete(action: { type: string; params: Record<string, unknown> }) {
  return RuleActionSchema.safeParse(action).success;
}
